import React, { useState, useEffect, useMemo, useRef } from "react";

import { useLocation } from "react-router-dom";

import * as Material from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import { DataGrid } from "@mui/x-data-grid";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddIcon from "@mui/icons-material/Add";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import HubIcon from "@mui/icons-material/Hub";

import { sendMessageTo } from "@contexts/ChatContext";

import "dayjs/locale/pt-br";

import api from "@helpers/api.js";

import Screen from "@components/Screen";
import Pagination from "@components/Pagination";

import "./styles.css";

import { useCommunity } from "contexts/community";

export default function UsersSearch({ onFilter, filterRef }) {
  const { communities } = useCommunity();

  function handleSubmitText(text) {
    filterRef.current.search_string = text;
    onFilter(filterRef.current);
  }
  function handleSubmitFilters(v) {
    filterRef.current.community_ids = v.community_ids
      ? [...v.community_ids]
      : [...communities.map((c) => c.id)];
    filterRef.current.subscription_status = v.subscriptionStatus;
    onFilter(filterRef.current);
  }

  return (
    <div className="users-search-box">
      <SearchInput onSubmit={handleSubmitText} />
      <Filters
        onSubmit={handleSubmitFilters}
        initialFilters={filterRef.current}
      />
    </div>
  );
}

const SearchInput = ({ onSubmit }) => {
  const [inputValue, setInputValue] = useState("");
  const [debouncedValue, setDebouncedValue] = useState(inputValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (debouncedValue !== inputValue) setDebouncedValue(inputValue);
    }, 2000);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue]);

  useEffect(() => {
    if (debouncedValue && inputValue.trim()) {
      onSubmit(debouncedValue);
    }
  }, [debouncedValue]);

  return (
    <Material.TextField
      label="Buscar participante"
      id="user-search"
      fullWidth
      onBlur={(e) => onSubmit(e.target.value.trim())}
      onInput={(e) => setInputValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          onSubmit(e.target.value.trim());
        }
      }}
      value={inputValue}
      style={{ maxWidth: 380 }}
      InputProps={{
        placeholder: "Insira um nome, email ou telefone",
        endAdornment: (
          <Material.InputAdornment position="start">
            <SearchIcon />
          </Material.InputAdornment>
        ),
      }}
    />
  );
};

const Filters = ({ onSubmit, initialFilters }) => {
  const { communities } = useCommunity();

  const [communitiesChecked, setCommunitiesChecked] = useState(
    initialFilters.community_ids || [...communities.map((c) => c.id)]
  );
  const [statusActive, setStatusActive] = useState("*");

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    handleSubmit();
  }, [statusActive]);

  const statuses = [
    {
      key: "*",
      labels: {
        multiple: "Todas",
        alone: "Todas",
      },
    },
    {
      key: "at_least_one_active",
      labels: {
        multiple: "Pelo menos uma ativa",
        alone: "Ativa",
      },
    },
    {
      key: "none_active",
      labels: {
        multiple: "Inativa",
        alone: "Inativa",
      },
    },
  ];

  function handleSubmit() {
    onSubmit({
      community_ids: communitiesChecked,
      subscriptionStatus: statusActive,
    });
  }

  function handleChangeCommunitiesSelected(v) {
    let value = v.target.value;

    setCommunitiesChecked([...value]);
  }
  function handleChangeSubscriptionStatuses(v) {
    setStatusActive(v.target.value);
  }

  function getCommunityFilterLabel() {
    return isAllCommunitiesChecked
      ? "Todas"
      : communities
          ?.filter((p) => communitiesChecked?.includes(p?.id))
          ?.map((p) => p.name)
          ?.join(", ") || "Todos";
  }
  function getSubscriptionLabel() {
    return statuses.filter((s) => s.key === statusActive)[0].labels[
      isAllCommunitiesChecked ? "multiple" : "alone"
    ];
  }

  const isAllCommunitiesChecked =
    communitiesChecked.length === communities.length;

  return (
    <div className="filter-chips subscription-filter-container mt-2">
      <Material.Chip
        label={
          <div className="subscription-filter">
            <Material.FormControl sx={{ maxWidth: 350 }}>
              <Material.Select
                onClose={handleSubmit}
                multiple
                value={communitiesChecked}
                onChange={handleChangeCommunitiesSelected}
                input={
                  <Material.OutlinedInput className="chip-input" label="Tag" />
                }
                renderValue={(selected) => (
                  <Chip.Title
                    title="Comunidades"
                    subtitle={getCommunityFilterLabel()}
                  />
                )}
              >
                {/*<Material.MenuItem value="*">
                  <Material.Checkbox checked={isAllCommunitiesChecked} />
                  <Material.ListItemText primary="Todos" />
                </Material.MenuItem>

                <Material.Divider>ou</Material.Divider>*/}

                {communities.map(({ id, name }) => (
                  <Material.MenuItem key={name} value={id}>
                    <Material.Checkbox
                      checked={
                        communitiesChecked?.includes(id) ||
                        isAllCommunitiesChecked
                      }
                    />
                    <Material.ListItemText primary={name} />
                  </Material.MenuItem>
                ))}
              </Material.Select>
            </Material.FormControl>

            <Material.Divider flexItem orientation="vertical" />

            <Material.FormControl>
              <Material.Select
                value={statusActive}
                onChange={handleChangeSubscriptionStatuses}
                input={
                  <Material.OutlinedInput className="chip-input" label="Tag" />
                }
                renderValue={(selected) => (
                  <Chip.Title
                    title="Status da assinatura"
                    subtitle={getSubscriptionLabel()}
                  />
                )}
              >
                {statuses.map(({ key, labels }) => (
                  <Material.MenuItem key={key} value={key}>
                    <Material.ListItemText
                      primary={
                        labels[isAllCommunitiesChecked ? "multiple" : "alone"]
                      }
                    />
                  </Material.MenuItem>
                ))}
              </Material.Select>
            </Material.FormControl>
          </div>
        }
        variant="outlined"
      />
    </div>
  );
};

const Chip = {
  Title: function ({ title, subtitle }) {
    return (
      <>
        <span className="chip-title">{title}</span>: {subtitle}
      </>
    );
  },
};
